/** Lojas da empresa (mesmo CNPJ): cada uma com seu estoque; cada conexão com um canal vende o estoque de uma loja. */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { StoreRow } from '@berytus/contracts';
import { api, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { Modal } from './CatalogSettings.tsx';
import { can } from '@berytus/contracts';

const CHANNEL: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', ifood: 'iFood', '99food': '99Food' };
const slug = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

export function StoresPage() {
  const me = useMe();
  const canEdit = can(me.tenant.access, 'settings', 'write') && !me.impersonating;
  const q = useQuery({ queryKey: ['stores'], queryFn: () => api<StoreRow[]>('/stores') });
  const [editing, setEditing] = useState<StoreRow | 'new' | null>(null);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Lojas e estoques</h1>
          <p>Lojas da mesma empresa (mesmo CNPJ), cada uma com o seu estoque. Cada conexão com um canal vende o estoque de uma loja: troque em Integração de canais. Outro CNPJ é outra conta na Berytus.</p>
        </div>
        {canEdit && <button className="btn primary" type="button" onClick={() => setEditing('new')}><Plus size={16} /> Nova loja</button>}
      </div>
      <div className="panel flush">
        {q.isPending ? <Loading /> : q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <thead><tr><th>Loja</th><th>Código (API)</th><th>Cidade</th><th className="num">Produtos com estoque</th><th>Canais vendendo</th><th>Situação</th><th /></tr></thead>
              <tbody>
                {q.data.map((s) => (
                  <tr key={s.id}>
                    <td><b>{s.name}</b>{s.notes && <div className="small muted">{s.notes}</div>}</td>
                    <td className="code">{s.code}</td>
                    <td>{s.city ? `${s.city}${s.state ? `/${s.state}` : ''}` : '—'}</td>
                    <td className="num">{int(s.products)}</td>
                    <td className="small">{s.connections.length ? s.connections.map((c) => `${CHANNEL[c.channel] ?? c.channel} · ${c.name ?? 'conta'}`).join(', ') : <span className="muted">nenhum</span>}</td>
                    <td>{s.active ? <Tag tone="ok">ativa</Tag> : <Tag tone="off">inativa</Tag>}</td>
                    <td style={{ textAlign: 'right' }}>{canEdit && <button className="btn sm" type="button" onClick={() => setEditing(s)}>Editar</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="panel small" style={{ marginTop: 16 }}>
        <b>Estoque pela API:</b> envie o código da loja em cada item: <span className="code">{'{"sku": "123", "quantity": 7, "storeCode": "zona-leste"}'}</span>.
        Sem código, o estoque vai para a loja principal. Veja <Link to="/configuracoes/api">Integração por API</Link>.
      </div>
      {editing && <StoreForm s={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function StoreForm({ s, onClose }: { s: StoreRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ code: s?.code ?? '', codeTouched: !!s, name: s?.name ?? '', city: s?.city ?? '', state: s?.state ?? '', postalCode: s?.postalCode ?? '', notes: s?.notes ?? '', active: s?.active ?? true });
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name.trim(), city: f.city.trim() || null, state: f.state.trim() || null, postalCode: f.postalCode.trim() || null, notes: f.notes.trim() || null, active: f.active };
      return s ? api(`/stores/${s.id}`, { method: 'PUT', json: body }) : api('/stores', { method: 'POST', json: { ...body, code: f.code } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['stores'] }); qc.invalidateQueries({ queryKey: ['channels'] }); onClose(); },
  });
  return (
    <Modal title={s ? `Editar ${s.name}` : 'Nova loja'} onClose={onClose}>
      <div className="form-grid">
        <div className="field"><label htmlFor="st-name">Nome</label>
          <input className="input" id="st-name" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, code: f.codeTouched ? f.code : slug(e.target.value) })} placeholder="Dark Zona Leste" /></div>
        <div className="field"><label htmlFor="st-code">Código (usado pelo ERP na API)</label>
          <input className="input code" id="st-code" disabled={!!s} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value, codeTouched: true })} />
          <span className="small muted">{s ? 'O código não muda depois de criado.' : 'Minúsculas, números e hífen. Use o mesmo código da filial no seu ERP.'}</span></div>
        <div className="field"><label htmlFor="st-city">Cidade</label><input className="input" id="st-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
        <div className="field"><label htmlFor="st-uf">UF</label><input className="input" id="st-uf" maxLength={2} value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase() })} /></div>
        <div className="field"><label htmlFor="st-zip">CEP</label><input className="input" id="st-zip" value={f.postalCode} onChange={(e) => setF({ ...f, postalCode: e.target.value })} /></div>
        <div className="field"><label htmlFor="st-notes">Observação</label><input className="input" id="st-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
      </div>
      {s && s.code !== 'principal' && (
        <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Loja ativa
        </label>
      )}
      {save.error && <ErrorBox error={save.error} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
        <button className="btn primary" type="button" disabled={f.name.trim().length < 2 || (!s && !f.code) || save.isPending} onClick={() => save.mutate()}>Salvar</button>
      </div>
    </Modal>
  );
}
